/**
 * Email Thread View - Display thread with all messages
 *
 * Features:
 * - Expandable message cards
 * - AI analysis display
 * - Reply/Forward actions
 * - Attachment downloads
 * - Thread Intelligence sidebar with entities
 * - Entity highlighting in message body
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import emailApi, {
  EmailMessage,
  EmailThreadPreview,
  EmailAttachment,
  ThreadIntelligence,
  AnnotatedMessage,
} from '@/services/emailApi';
import EmailContent from './EmailContent';
import ThreadIntelligencePanel from './email/ThreadIntelligencePanel';
import EntityHighlight, { EntityHighlightLegend } from './email/EntityHighlight';

interface EmailThreadViewProps {
  threadId: string;
  onClose: () => void;
  onReply?: (message: EmailMessage) => void;
  onReplyAll?: (message: EmailMessage) => void;
  onForward?: (message: EmailMessage) => void;
  onMarkRead?: (threadId: string) => void;
}

const EmailThreadView: React.FC<EmailThreadViewProps> = ({
  threadId,
  onClose,
  onReply,
  onReplyAll,
  onForward,
  onMarkRead,
}) => {
  const { t } = useTranslation('email');
  const [thread, setThread] = useState<EmailThreadPreview | null>(null);
  const [messages, setMessages] = useState<EmailMessage[]>([]);
  const [expandedMessages, setExpandedMessages] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Intelligence panel state
  const [intelligence, setIntelligence] = useState<ThreadIntelligence | null>(null);
  const [intelligenceLoading, setIntelligenceLoading] = useState(false);
  const [showIntelligence, setShowIntelligence] = useState(false);

  // Entity annotations per message (stores full API response including is_html)
  const [annotationData, setAnnotationData] = useState<Record<string, AnnotatedMessage>>({});
  const [showHighlights, setShowHighlights] = useState(true);

  useEffect(() => {
    loadThread();
    loadIntelligence();
  }, [threadId]);

  const loadThread = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await emailApi.threads.get(threadId);
      setThread(data.thread);
      setMessages(data.messages);
      // All messages collapsed by default
      setExpandedMessages(new Set());
      // Mark as read and notify parent
      await emailApi.threads.markRead(threadId);
      onMarkRead?.(threadId);
    } catch (err) {
      setError(t('thread.load_failed'));
      console.error('Error loading thread:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadIntelligence = async () => {
    setIntelligenceLoading(true);
    try {
      const data = await emailApi.intelligence.getThreadIntelligence(threadId);
      // Transform API response to match component interface
      const transformedData: ThreadIntelligence = {
        ...data,
        entities: {
          people: data.entities?.people || [],
          organizations: data.entities?.organizations || [],
          dates: data.entities?.dates || [],
          amounts: data.entities?.amounts || [],
        },
      };
      setIntelligence(transformedData);
    } catch (err) {
      console.error('Error loading intelligence:', err);
      // Don't show error - intelligence is optional
    } finally {
      setIntelligenceLoading(false);
    }
  };

  const loadMessageAnnotations = async (messageId: string) => {
    if (annotationData[messageId]) return; // Already loaded

    try {
      const data = await emailApi.intelligence.getAnnotatedMessage(messageId);
      setAnnotationData((prev) => ({
        ...prev,
        [messageId]: data,
      }));
    } catch (err) {
      console.error('Error loading annotations:', err);
    }
  };

  const handleActionItemToggle = async (actionItem: { id?: string; text: string; status: string }, completed: boolean) => {
    if (!actionItem.id) return;
    try {
      await emailApi.draftAi.updateActionItem(actionItem.id, completed ? 'completed' : 'pending');
      // Refresh intelligence to get updated action items
      loadIntelligence();
    } catch (err) {
      console.error('Error updating action item:', err);
    }
  };

  const handleEntityClick = (entity: { name: string; type?: string; entity_type?: string; email?: string }) => {
    // Navigate to contact or COGITO entity
    console.log('Entity clicked:', entity);
    // TODO: Open contact detail or COGITO view
  };

  const toggleExpanded = (messageId: string) => {
    setExpandedMessages((prev) => {
      const next = new Set(prev);
      if (next.has(messageId)) {
        next.delete(messageId);
      } else {
        next.add(messageId);
        // Load annotations when expanding
        loadMessageAnnotations(messageId);
      }
      return next;
    });
  };

  const handleDownloadAttachment = async (message: EmailMessage, attachment: EmailAttachment) => {
    try {
      const url = await emailApi.messages.getAttachmentUrl(message.id, attachment.id);
      window.open(url, '_blank');
    } catch (err: any) {
      console.error('Failed to get attachment URL:', err);
      // Show user-friendly error message
      alert(err.message || t('thread.download_failed'));
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays < 7) {
      return date.toLocaleDateString('en-US', { weekday: 'short', hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-gray-500">{t('thread.loading')}</p>
        </div>
      </div>
    );
  }

  if (error || !thread) {
    return (
      <div className="flex-1 flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <p className="text-red-500 font-medium">{error || t('thread.not_found')}</p>
          <button
            onClick={onClose}
            className="mt-4 px-4 py-2 text-sm text-blue-600 hover:underline"
          >
            {t('navigation.go_back')}
          </button>
        </div>
      </div>
    );
  }

  // Transform intelligence data for panel component
  // API returns flat arrays (people, organizations) not nested under 'entities'
  const panelIntelligence = intelligence
    ? {
        thread_id: intelligence.thread_id,
        message_count: intelligence.message_count || messages.length,
        first_message_at: intelligence.first_message_at,
        last_message_at: intelligence.last_message_at,
        // Use flat arrays from API (people, organizations, etc.) with fallback to nested entities
        people: (intelligence.people || intelligence.entities?.people || []).map((p) => ({
          name: p.name,
          entity_type: p.entity_type || p.type || 'PERSON',
          mention_count: p.mention_count || p.email_count || 1,
          cogito_id: p.cogito_id,
          contact_id: p.contact_id,
          role: p.role,
          email: p.email,
        })),
        organizations: (intelligence.organizations || intelligence.entities?.organizations || []).map((o) => ({
          name: o.name,
          entity_type: o.entity_type || o.type || 'ORGANIZATION',
          mention_count: o.mention_count || o.email_count || 1,
          cogito_id: o.cogito_id,
          contact_id: o.contact_id,
        })),
        dates: (intelligence.dates || intelligence.entities?.dates || []).map((d) => ({
          text: d.text,
          parsed: d.parsed,
          context: d.context,
          is_deadline: d.is_past === false, // Dates in future are likely deadlines
        })),
        amounts: (intelligence.amounts || intelligence.entities?.amounts || []).map((a) => ({
          value: a.value,
          formatted: a.formatted,
          currency: a.currency,
          context: a.context,
        })),
        // Action items: API returns 'text' field, not 'action'
        action_items: (intelligence.action_items || []).map((item) => ({
          id: item.id,
          text: item.text || item.action || '',  // Use 'text' from API, fallback to 'action'
          assignee: item.assignee,
          due_date: item.due_date || item.deadline,
          status: item.status,
        })),
        summary: intelligence.summary,
        prognosis: intelligence.prognosis,
        overall_sentiment: intelligence.overall_sentiment || intelligence.summary?.overall_sentiment,
        avg_priority: intelligence.avg_priority,
        requires_response: intelligence.requires_response || false,
        participants: intelligence.participants || [],
      }
    : null;

  return (
    <div className="flex-1 flex bg-white overflow-hidden">
      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <div className="border-b border-gray-200 px-4 sm:px-6 py-4 flex-shrink-0">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <button
                onClick={onClose}
                className="mb-2 flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                {t('common:buttons.back')}
              </button>
              <h1 className="text-xl font-semibold text-gray-900 truncate">{thread.subject || t('common.no_subject')}</h1>
              <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
                <span>{t('thread.message_count', { count: messages.length })}</span>
                {thread.labels && thread.labels.length > 0 && (
                  <div className="flex items-center gap-1">
                    {thread.labels.map((label) => (
                      <span key={label} className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs">
                        {label}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Actions - Two rows */}
            <div className="flex flex-col gap-1 flex-shrink-0">
              {/* Row 1: Standard operations */}
              <div className="flex items-center gap-1">
                <button
                  onClick={() => emailApi.threads.toggleStar(threadId).then(loadThread)}
                  className={`p-2 rounded-lg transition ${
                    thread.is_starred ? 'text-yellow-500 hover:bg-yellow-50' : 'text-gray-400 hover:bg-gray-100'
                  }`}
                  title={t('thread.star')}
                >
                  <svg className={`w-5 h-5 ${thread.is_starred ? 'fill-current' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                  </svg>
                </button>
                <button
                  onClick={() => emailApi.threads.archive(threadId).then(onClose)}
                  className="p-2 text-gray-400 hover:bg-gray-100 rounded-lg transition"
                  title={t('thread.archive')}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                  </svg>
                </button>
                <button
                  onClick={() => {
                    if (confirm(t('thread.delete_confirm'))) {
                      emailApi.threads.delete(threadId).then(onClose);
                    }
                  }}
                  className="p-2 text-gray-400 hover:bg-red-50 hover:text-red-500 rounded-lg transition"
                  title={t('thread.delete')}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
              {/* Row 2: AI features */}
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setShowHighlights(!showHighlights)}
                  className={`p-2 rounded-lg transition ${
                    showHighlights ? 'text-purple-500 bg-purple-50' : 'text-gray-400 hover:bg-gray-100'
                  }`}
                  title={showHighlights ? t('thread.hide_highlights') : t('thread.show_highlights')}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                  </svg>
                </button>
                <button
                  onClick={() => setShowIntelligence(!showIntelligence)}
                  className={`p-2 rounded-lg transition ${
                    showIntelligence ? 'text-blue-500 bg-blue-50' : 'text-gray-400 hover:bg-gray-100'
                  }`}
                  title={showIntelligence ? t('ai.hide_intelligence') : t('ai.show_intelligence')}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4">
        {/* AI Analysis from latest message - at the very top */}
        {(() => {
          // Find the latest message with AI analysis (sorted by received_at)
          const latestWithAnalysis = [...messages]
            .sort((a, b) => new Date(b.received_at).getTime() - new Date(a.received_at).getTime())
            .find((m) => m.ai_analysis?.summary);

          if (!latestWithAnalysis?.ai_analysis) return null;

          const analysis = latestWithAnalysis.ai_analysis;
          const priority = analysis.priority_score || 3;
          const isMeeting = thread.ai_summary?.is_meeting_request;
          const requiresResponse = thread.ai_summary?.requires_response;
          const isAnomaly = analysis.anomaly_detected;
          const sentiment = analysis.sentiment;

          // Determine situation-based color scheme
          type ColorScheme = {
            bg: string;
            border: string;
            headerText: string;
            bodyText: string;
            bulletColor: string;
            chipBg: string;
            chipText: string;
          };

          let colors: ColorScheme;
          let situationLabel = '';

          if (isAnomaly || priority >= 5) {
            // Critical - red
            colors = {
              bg: 'bg-red-50',
              border: 'border-red-200',
              headerText: 'text-red-700',
              bodyText: 'text-red-900',
              bulletColor: 'text-red-400',
              chipBg: 'bg-red-500',
              chipText: 'text-white',
            };
            situationLabel = t('ai.critical');
          } else if (priority >= 4 || requiresResponse) {
            // High priority - orange
            colors = {
              bg: 'bg-orange-50',
              border: 'border-orange-200',
              headerText: 'text-orange-700',
              bodyText: 'text-orange-900',
              bulletColor: 'text-orange-400',
              chipBg: 'bg-orange-500',
              chipText: 'text-white',
            };
            situationLabel = requiresResponse ? t('ai.response_needed') : t('ai.high');
          } else if (isMeeting) {
            // Meeting - indigo
            colors = {
              bg: 'bg-indigo-50',
              border: 'border-indigo-200',
              headerText: 'text-indigo-700',
              bodyText: 'text-indigo-900',
              bulletColor: 'text-indigo-400',
              chipBg: 'bg-indigo-500',
              chipText: 'text-white',
            };
            situationLabel = t('ai.meeting');
          } else if (sentiment === 'negative') {
            // Negative sentiment - amber
            colors = {
              bg: 'bg-amber-50',
              border: 'border-amber-200',
              headerText: 'text-amber-700',
              bodyText: 'text-amber-900',
              bulletColor: 'text-amber-400',
              chipBg: 'bg-amber-500',
              chipText: 'text-white',
            };
            situationLabel = t('ai.attention');
          } else if (sentiment === 'positive') {
            // Positive - green
            colors = {
              bg: 'bg-green-50',
              border: 'border-green-200',
              headerText: 'text-green-700',
              bodyText: 'text-green-900',
              bulletColor: 'text-green-400',
              chipBg: 'bg-green-500',
              chipText: 'text-white',
            };
            situationLabel = t('ai.positive');
          } else {
            // Neutral/info - blue
            colors = {
              bg: 'bg-blue-50',
              border: 'border-blue-200',
              headerText: 'text-blue-700',
              bodyText: 'text-blue-900',
              bulletColor: 'text-blue-400',
              chipBg: 'bg-blue-500',
              chipText: 'text-white',
            };
            situationLabel = t('ai.info');
          }

          return (
            <div className={`p-4 ${colors.bg} border ${colors.border} rounded-lg space-y-3`}>
              {/* AI Analysis Header with Priority Chip */}
              <div className="flex items-center justify-between">
                <div className={`flex items-center gap-2 text-sm font-medium ${colors.headerText}`}>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  {t('ai.ai_analysis')}
                  {situationLabel && (
                    <span className={`ml-2 px-2 py-0.5 text-xs rounded ${colors.chipBg} ${colors.chipText}`}>
                      {situationLabel}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {/* Category badge */}
                  <span className={`px-2 py-0.5 text-xs rounded ${colors.bg} ${colors.headerText} border ${colors.border}`}>
                    {analysis.category}
                  </span>
                  {/* Priority chip - circular like email list */}
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${colors.chipBg} ${colors.chipText}`}>
                    {priority >= 4 ? (
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    ) : (
                      priority
                    )}
                  </div>
                </div>
              </div>

              {/* Summary */}
              <p className={`text-sm ${colors.bodyText}`}>{analysis.summary}</p>

              {/* Key Points */}
              {analysis.key_points && analysis.key_points.length > 0 && (
                <div className="space-y-1">
                  <div className={`text-xs font-medium ${colors.headerText}`}>{t('ai.key_points')}:</div>
                  <ul className={`text-sm ${colors.bodyText} space-y-1`}>
                    {analysis.key_points.map((point, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <span className={`${colors.bulletColor} mt-1`}>•</span>
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Anomaly Warning - extra emphasis when anomaly detected */}
              {analysis.anomaly_detected && analysis.anomaly_details && (
                <div className="p-2 bg-red-100 border border-red-300 rounded text-sm">
                  <div className="flex items-center gap-2 text-red-800 font-medium">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    {analysis.anomaly_details.type} ({analysis.anomaly_details.severity})
                  </div>
                  <p className="text-red-700 mt-1">{analysis.anomaly_details.description}</p>
                </div>
              )}
            </div>
          );
        })()}

        {/* All Action Items */}
        {(() => {
          // Collect all action items from all messages
          const allActionItems = messages.flatMap((msg) =>
            (msg.ai_analysis?.action_items || []).map((item, itemIdx) => ({
              ...item,
              messageFrom: msg.from_name || msg.from_email,
              messageDate: msg.received_at,
              key: `${msg.id}-${itemIdx}`,
            }))
          );

          if (allActionItems.length === 0) return null;

          return (
            <div className="p-4 bg-purple-50 border border-purple-100 rounded-lg">
              <div className="flex items-center gap-2 text-sm font-medium text-red-700 mb-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
                {t('ai.all_action_items')} ({allActionItems.length})
              </div>
              <div className="space-y-2">
                {allActionItems.map((item) => (
                  <div key={item.key} className="flex items-start gap-2 text-sm bg-white/50 p-2 rounded">
                    <input type="checkbox" className="mt-0.5 w-4 h-4 text-red-600 border-gray-300 rounded" />
                    <div className="flex-1 min-w-0">
                      <span className="text-gray-900">{item.action}</span>
                      <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                        <span>{t('thread.from')}: {item.messageFrom}</span>
                        {item.deadline && (
                          <span className="text-orange-600">{t('ai.due')}: {item.deadline}</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        {/* Messages in reverse order - newest first */}
        {[...messages].reverse().map((message) => {
          const isExpanded = expandedMessages.has(message.id);

          return (
            <div
              key={message.id}
              className={`border rounded-lg transition-shadow ${
                isExpanded ? 'shadow-md' : 'hover:shadow-sm'
              }`}
            >
              {/* Message Header (always visible) */}
              <div
                className="px-4 py-3 cursor-pointer"
                onClick={() => toggleExpanded(message.id)}
              >
                <div className="flex items-start gap-3">
                  {/* Avatar */}
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-semibold flex-shrink-0">
                    {message.from_name?.[0]?.toUpperCase() || message.from_email[0].toUpperCase()}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="font-medium text-gray-900 truncate">
                          {message.from_name || message.from_email}
                        </span>
                        {!isExpanded && (
                          <span className="text-sm text-gray-500 truncate hidden sm:inline">
                            &lt;{message.from_email}&gt;
                          </span>
                        )}
                      </div>
                      <span className="text-sm text-gray-500 flex-shrink-0">
                        {formatDate(message.received_at)}
                      </span>
                    </div>

                    {isExpanded ? (
                      <div className="mt-1 text-sm text-gray-600">
                        <div className="flex items-center gap-1">
                          <span className="text-gray-500">{t('compose.to')}:</span>
                          <span className="truncate">
                            {message.to.map(t => t.name || t.email).join(', ')}
                          </span>
                        </div>
                        {message.cc && message.cc.length > 0 && (
                          <div className="flex items-center gap-1">
                            <span className="text-gray-500">{t('compose.cc')}:</span>
                            <span className="truncate">
                              {message.cc.map(t => t.name || t.email).join(', ')}
                            </span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-600 truncate">
                        {message.snippet || message.body_text?.slice(0, 100) || t('thread.no_content')}
                      </p>
                    )}
                  </div>

                  {/* Expand/Collapse indicator */}
                  <svg
                    className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>

              {/* Message Body (expanded) */}
              {isExpanded && (
                <div className="border-t border-gray-100">
                  {/* Entity Highlight Legend - inside expanded message */}
                  {showHighlights && (
                    <div className="px-4 pt-3">
                      <EntityHighlightLegend className="opacity-70" />
                    </div>
                  )}

                  {/* Body - Using EntityHighlight when enabled, otherwise safe iframe renderer */}
                  <div className="px-4 py-4">
                    {(() => {
                      const msgAnnotation = annotationData[message.id];
                      const hasAnnotations = msgAnnotation && msgAnnotation.annotations?.length > 0;

                      if (showHighlights && hasAnnotations) {
                        // Use EntityHighlight with proper content and HTML flag
                        return (
                          <EntityHighlight
                            content={msgAnnotation.original_content}
                            annotations={msgAnnotation.annotations}
                            isHtml={msgAnnotation.is_html}
                            onEntityClick={handleEntityClick}
                            showTooltips={true}
                            className="text-sm text-gray-700 leading-relaxed"
                          />
                        );
                      }

                      // Default: use EmailContent for normal rendering
                      return (
                        <EmailContent
                          html={message.body_html}
                          text={message.body_text}
                          allowExternalImages={false}
                        />
                      );
                    })()}
                  </div>

                  {/* Attachments */}
                  {message.attachments && message.attachments.length > 0 && (
                    <div className="px-4 pb-4">
                      <div className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                        </svg>
                        {t('thread.attachment_count', { count: message.attachments.length })}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {message.attachments.map(att => (
                          <button
                            key={att.id}
                            onClick={() => handleDownloadAttachment(message, att)}
                            className="flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm transition"
                          >
                            <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                            </svg>
                            <span className="truncate max-w-[150px]">{att.filename}</span>
                            <span className="text-gray-400">({att.size_bytes ? formatFileSize(att.size_bytes) : t('thread.unknown_size')})</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* AI Analysis */}
                  {message.ai_analysis && (
                    <div className="mx-4 mb-4 p-3 bg-purple-50 rounded-lg">
                      <div className="text-xs font-medium text-purple-700 mb-1">{t('ai.ai_analysis')}</div>
                      <p className="text-sm text-purple-900 mb-2">{message.ai_analysis.summary}</p>

                      {message.ai_analysis.key_points && message.ai_analysis.key_points.length > 0 && (
                        <ul className="text-xs text-purple-800 space-y-1 mb-2">
                          {message.ai_analysis.key_points.map((point, i) => (
                            <li key={i} className="flex items-start gap-1">
                              <span className="text-purple-400">•</span>
                              {point}
                            </li>
                          ))}
                        </ul>
                      )}

                      {message.ai_analysis.action_items && message.ai_analysis.action_items.length > 0 && (
                        <div className="border-t border-purple-200 pt-2 mt-2">
                          <div className="text-xs font-medium text-purple-700 mb-1">{t('ai.action_items')}</div>
                          {message.ai_analysis.action_items.map((item, i) => (
                            <div key={i} className="flex items-center gap-2 text-xs text-purple-800">
                              <input type="checkbox" className="w-3 h-3" />
                              <span>{item.action}</span>
                              {item.deadline && (
                                <span className="text-purple-500">{t('ai.due')}: {item.deadline}</span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      {message.ai_analysis.anomaly_detected && message.ai_analysis.anomaly_details && (
                        <div className="mt-2 p-2 bg-red-100 border border-red-200 rounded text-xs text-red-700">
                          <span className="font-medium">{t('ai.warning')}:</span> {message.ai_analysis.anomaly_details.description}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Message Actions */}
                  <div className="px-4 py-3 bg-gray-50 border-t border-gray-100 flex items-center gap-2">
                    <button
                      onClick={() => onReply?.(message)}
                      className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                      </svg>
                      {t('compose.reply')}
                    </button>
                    {/* Show Reply All only if there are multiple recipients */}
                    {(message.to?.length > 1 || (message.cc && message.cc.length > 0)) && (
                      <button
                        onClick={() => onReplyAll?.(message)}
                        className="flex items-center gap-1 px-3 py-1.5 bg-blue-100 text-blue-700 text-sm rounded hover:bg-blue-200 transition"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                        </svg>
                        {t('compose.reply_all')}
                      </button>
                    )}
                    <button
                      onClick={() => onForward?.(message)}
                      className="flex items-center gap-1 px-3 py-1.5 bg-gray-200 text-gray-700 text-sm rounded hover:bg-gray-300 transition"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                      </svg>
                      {t('compose.forward')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        </div>
      </div>

      {/* Intelligence Sidebar */}
      {showIntelligence && (
        <ThreadIntelligencePanel
          threadId={threadId}
          intelligence={panelIntelligence}
          isLoading={intelligenceLoading}
          onEntityClick={(entity, type) => handleEntityClick({ ...entity, type })}
          onActionItemToggle={handleActionItemToggle}
        />
      )}
    </div>
  );
};

export default EmailThreadView;
